import React from 'react'
import PropTypes from 'prop-types'
import { ariaProps, dataProps, onProps } from '@ds/react-utils'
import { useTranslate } from '@ds/comp-private'
import {
  Browser,
  BrowserSmall,
  ExternalLink,
  ExternalLinkSmall,
} from '@ds/icons'
import { TextEllipsis } from '../TextEllipsis'
import { ConditionalTag } from '../../internal/components/ConditionalTag'
import { warnInvalidThemeProp } from '../../logging'
import { CustomPropTypes } from '../../support'
import { useDsUiDataProps, useThemeStyles, useIsInk } from '../../theming'
import type {
  AnchorOrButtonForwardRef,
  AriaAttributes,
  EventListenerProps,
} from '../../types'
import { AnchorTarget, anchorTargets } from '../../variables'
import styles from './styles'

export const linkKinds = ['default', 'subtle'] as const
export const linkSizes = ['small', 'medium'] as const
export const linkIconKinds = ['new-window', 'external'] as const

export type LinkKind = (typeof linkKinds)[number]
export type LinkSize = (typeof linkSizes)[number]
export type LinkIconKinds = (typeof linkIconKinds)[number]
export type LinkTarget = AnchorTarget

export interface LinkProps
  extends EventListenerProps<HTMLButtonElement | HTMLAnchorElement>,
    AriaAttributes {
  /**
   * The text to present to assistive devices in order to identify the Link.
   * `accessibilityText` replaces the visible `text` for screen reader users.
   */
  accessibilityText?: string
  /**
   * Accepts aria attributes.
   */
  'aria-.*'?: string
  /**
   * Accepts custom data attributes.
   */
  'data-.*'?: string
  'data-qa'?: string
  /**
   * Disable the Link component.
   *
   * (only valid when it renders a button, this is ignored if an href is provided)
   */
  disabled?: boolean
  /**
   * The discrete styled link. For standalone Links outside of text copy.
   * Removes the text-decoration underline and increases the tap target.
   */
  discrete?: boolean
  /**
   * A React ref to assign to the HTML node representing the Link element.
   */
  forwardedRef?: AnchorOrButtonForwardRef
  /**
   * If href is provided then it will render an anchor, otherwise a button.
   */
  href?: string
  /**
   * Display an icon after the link text, providing more contextual information for the user where the
   * link will redirect them.
   *
   * If the link opens an external page (outside of the current product), set the prop to `external`.
   *
   * If the link opens in a new window (but within the Docusign product), set the prop to `new-window` -
   * this will become default behavior in a future major release.
   *
   * Recommended for optimal accessibility.
   * Only in Ink.
   */
  icon?: LinkIconKinds
  /**
   * An Olive theme only prop to display lighter text for more contrast on dark backgrounds
   */
  isLightText?: boolean
  /**
   * Whether the Link displays as default or subtle.
   */
  kind?: LinkKind
  /**
   * The relationship of the linked URL of a anchor as space-separated link types.
   *
   * Reference: https://developer.mozilla.org/en-US/docs/Web/HTML/Link_types
   */
  rel?: string
  /**
   * The size of the Link. This is only avaiable with discrete links.
   */
  size?: LinkSize
  /**
   * @deprecated This will become default behavior in a future major release, with
   * the `icon` prop set to `new-window`.
   *
   * Display an icon when link opens in a new window (ie. target = '_blank').
   * If link opens an external page (outside of the current product), set the `icon` prop set to `external` instead.
   * Recommended true for optimal accessibility.
   * Only in Ink.
   */
  showIconForNewWindow?: boolean
  /**
   * The HTML link target (if an href is _not_ provided, this is ignored).
   * When target="_blank" use rel="noreferrer" or rel="noopener" to avoid the vulnerability.
   */
  target?: AnchorTarget
  /**
   * The text label for the Link.
   */
  text: React.ReactNode
}

/**
 * Links allow users to navigate to other locations.
 */
export function Link({
  accessibilityText,
  'data-qa': dataQa,
  disabled = false,
  discrete = false,
  forwardedRef,
  href,
  icon,
  isLightText,
  kind = 'default',
  onClick,
  rel,
  size = 'medium',
  showIconForNewWindow = false,
  target,
  text,
  ...restProps
}: LinkProps) {
  const translate = useTranslate()
  const isInk = useIsInk()

  const dsUiDataProps = useDsUiDataProps(Link, 'Olive-specific-prop', {
    isLightText,
  })

  if (isLightText && isInk) {
    warnInvalidThemeProp('Link', 'isLightText', 'Ink')
  }

  const isValidTextString = typeof text === 'string'
  const isTextWithTextEllipsis =
    React.isValidElement(text) && text.type === TextEllipsis

  const sx = useThemeStyles(styles, {
    size,
    kind,
    discrete,
    isLightText,
    isTextWithTextEllipsis,
  })

  const opensNewWindow = !!href && target === '_blank'
  const showNewWindowIcon =
    opensNewWindow && (icon === 'new-window' || showIconForNewWindow)
  const showExternalIcon = icon === 'external'
  const browserIcon = size === 'small' ? <BrowserSmall /> : <Browser />
  const externalIcon =
    size === 'small' ? <ExternalLinkSmall /> : <ExternalLink />

  const iconNode =
    showExternalIcon || showNewWindowIcon ? (
      <span css={sx.default.icon} data-qa={dataQa && `${dataQa}-icon`}>
        {showExternalIcon ? externalIcon : browserIcon}
      </span>
    ) : undefined

  const linkNode = (
    <span
      data-qa={dataQa && `${dataQa}-text`}
      css={{ textDecoration: 'inherit' }}
    >
      {text}
      {isInk && iconNode}
    </span>
  )

  const getA11yLinkText = (): string => {
    if (accessibilityText) {
      return accessibilityText
    }
    // If it gets to this point, we know text is a valid string
    return text as string
  }

  const getAriaLabel = (): string | undefined => {
    if (opensNewWindow && isValidTextString) {
      return translate('OLIVE:LINK_OPENS_IN_NEW_WINDOW:A11Y', {
        LINK: getA11yLinkText(),
      })
    }

    if (showExternalIcon && isValidTextString) {
      return translate('OLIVE:LINK_OPENS_EXTERNAL_PAGE:A11Y', {
        LINK: getA11yLinkText(),
      })
    }

    return accessibilityText
  }

  return (
    <ConditionalTag
      aria-label={getAriaLabel()}
      {...dsUiDataProps}
      {...ariaProps(restProps)}
      {...dataProps(restProps)}
      {...onProps(restProps)}
      css={sx.default.link}
      data-qa={dataQa}
      disabled={disabled}
      forceElement={discrete ? 'button' : 'span'}
      forwardedRef={forwardedRef}
      href={href}
      onClick={onClick}
      rel={rel}
      target={target}
    >
      {linkNode}
    </ConditionalTag>
  )
}

Link.kinds = linkKinds
Link.targets = anchorTargets

Link.propTypes = {
  accessibilityText: PropTypes.string,
  'aria-.*': PropTypes.string,
  'data-.*': PropTypes.string,
  disabled: PropTypes.bool,
  discrete: PropTypes.bool,
  forwardedRef: CustomPropTypes.ReactRef,
  href: PropTypes.string,
  icon: PropTypes.oneOf(linkIconKinds),
  isLightText: PropTypes.bool,
  kind: PropTypes.oneOf(linkKinds),
  'on[A-Z].*': PropTypes.func,
  onClick: PropTypes.func,
  rel: PropTypes.string,
  showIconForNewWindow: PropTypes.bool,
  size: PropTypes.oneOf(linkSizes),
  target: PropTypes.oneOf(anchorTargets),
  text: PropTypes.node.isRequired,
}

Link.displayName = 'Link'
